import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ServiceDetailPageContainer } from './ServiceDetailPage';
import { FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ServiceImage = styled.img`
  max-width: 200px;
  height: auto;
  border-radius: 0;
  margin: 0.5rem 0 1rem 2rem;
  box-shadow: none;
  float: right;

  @media (max-width: 768px) {
    float: none;
    margin: 1rem auto;
    max-width: 80%;
    display: block;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  color: #fff !important;
  margin-top: 0 !important;
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function WebDesign() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to exactly 11 digits
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDetailPageContainer>
      <h1>Kurumsal Web Tasarım</h1>
      <p>Şirketinizin dijital varlığını güçlendirecek modern, kullanıcı dostu ve etkileyici web siteleri tasarlıyoruz. Her projemizde estetikle işlevselliği bir araya getirerek markanızın çevrimiçi hedeflerine ulaşmasını sağlıyoruz.</p>
      
      <ServiceImage src="/media/demirer-ajans-1.png" alt="Web Tasarım Görseli 1" />

      <h2>Neden Kurumsal Web Tasarımı Önemlidir?</h2>
      <p>Günümüz dijital çağında, bir işletmenin çevrimiçi varlığı markanın güvenilirliği ve erişilebilirliği için hayati önem taşır. Profesyonel bir web sitesi, potansiyel müşterilerinize ulaşmanın, ürün ve hizmetlerinizi sergilemenin ve markanızın itibarını pekiştirmenin en etkili yollarından biridir.</p>

      <ServiceImage src="/media/demirer-ajans-2.png" alt="Web Tasarım Görseli 2" />

      <h3>Hizmetlerimiz Neleri Kapsar?</h3>
      <ul>
        <li><b>Özelleştirilmiş Tasarım:</b> Markanızın kimliğini ve hedeflerini yansıtan benzersiz tasarımlar.</li>
        <li><b>Kullanıcı Dostu Arayüz (UI/UX):</b> Ziyaretçilerin kolayca gezinebileceği, sezgisel ve keyifli bir kullanıcı deneyimi.</li>
        <li><b>Mobil Uyumluluk:</b> Tüm cihazlarda (masaüstü, tablet, mobil) mükemmel görünen ve çalışan responsive tasarımlar.</li>
        <li><b>SEO Uyumlu Altyapı:</b> Arama motorlarında üst sıralarda yer almanızı sağlayacak teknik SEO optimizasyonları.</li>
        <li><b>Hız ve Performans Optimizasyonu:</b> Yüksek hızlı yükleme süreleri ve kesintisiz performans.</li>
        <li><b>Güvenlik:</b> Web sitenizin güvenliğini sağlamak için en güncel protokoller ve önlemler.</li>
        <li><b>Kolay Yönetilebilir İçerik Yönetim Sistemi (CMS):</b> WordPress gibi popüler CMS platformları ile kolay içerik güncelleme imkanı.</li>
        <li><b>Bakım ve Destek:</b> Web sitenizin güncel ve sorunsuz çalışmasını sağlamak için sürekli teknik destek ve bakım hizmetleri.</li>
      </ul>

      <ServiceImage src="/media/demirer-ajans-3.png" alt="Web Tasarım Görseli 3" />

      <h2>Markanız İçin Nasıl Bir Web Sitesi Tasarlıyoruz?</h2>
      <p>Proje başlangıcından itibaren sizinle yakın işbirliği içinde çalışarak, ihtiyaçlarınızı ve beklentilerinizi tam olarak anlıyoruz. Hedef kitlenizin davranışlarını analiz ediyor, sektörünüzdeki eğilimleri takip ediyor ve bu bilgileri tasarıma entegre ediyoruz.</p>
      <p>Sonuç olarak, sadece görsel olarak çekici değil, aynı zamanda iş hedeflerinize ulaşmanıza yardımcı olacak stratejik bir web sitesi ortaya çıkarıyoruz. Dijital dünyada öne çıkmak ve rakiplerinizin bir adım önüne geçmek için bize güvenebilirsiniz.</p>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={formData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={formData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServiceDetailPageContainer>
  );
}

export default WebDesign;